import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CampaignCard from '../components/campaigns/CampaignCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import LocationPicker from '../components/ui/LocationPicker';
import {
  Search,
  SlidersHorizontal,
  Grid3X3,
  List,
  X,
  BadgeCheck,
  Shield,
  TrendingUp,
  Loader2,
  MapPin,
  Filter,
  ChevronDown,
  Check,
} from 'lucide-react';
import { categories, featuredCampaigns as mockCampaigns } from '../data/mock';
import { campaignAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const DiscoverPage = () => {
  const { t } = useLanguage();
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const { user } = useAuth();

  const locationQuery = searchParams.get('location') || '';
  const categoryFromUrl = searchParams.get('category') || '';
  const categoriesFromUrl = searchParams.get('categories') || ''; // Support comma-separated categories
  const subcategoryFromUrl = searchParams.get('subcategory') || '';
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchQuery);
  const [preferencesApplied, setPreferencesApplied] = useState(false);
  
  // Initialize category/subcategory from URL params or default to 'all'
  // Multi-select categories support
  const [selectedCategories, setSelectedCategories] = useState(() => {
    if (categoryId) return [categoryId];
    if (categoriesFromUrl) return categoriesFromUrl.split(',').map(c => c.trim());
    if (categoryFromUrl) return [categoryFromUrl];
    return [];
  });
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    subcategoryFromUrl || 'all'
  );
  const [sortBy, setSortBy] = useState('category');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    locationQuery ? { display: locationQuery } : null
  );
  const [filters, setFilters] = useState({
    verified: false,
    escrow: false,
    trending: false,
    minGoal: '',
    maxGoal: '',
    country: 'all',
  });
  
  // Check if user explicitly wants to see all campaigns (came from "View All" link)
  const showAllFromUrl = searchParams.get('all') === 'true';
  
  // Apply user preferences when user data loads (only once)
  // Skip if user explicitly wants to see all campaigns
  useEffect(() => {
    if (user && !preferencesApplied && !categoryId && !categoryFromUrl && !showAllFromUrl) {
      if (user.campaign_preferences && user.campaign_preferences.length > 0) {
        setSelectedCategories(user.campaign_preferences);
        setPreferencesApplied(true);
      } else if (user.preferred_category) {
        setSelectedCategories([user.preferred_category]);
        if (user.preferred_subcategory) {
          setSelectedSubcategory(user.preferred_subcategory);
        }
        setPreferencesApplied(true);
      }
    }
  }, [user, preferencesApplied, categoryId, categoryFromUrl, showAllFromUrl]);

  // Sync selectedCategories with URL changes
  useEffect(() => {
    // Priority: categoryId from route params > categories from query > category from query
    if (categoryId) {
      setSelectedCategories([categoryId]);
    } else if (categoriesFromUrl) {
      setSelectedCategories(categoriesFromUrl.split(',').map(c => c.trim()));
    } else if (categoryFromUrl) {
      setSelectedCategories([categoryFromUrl]);
    }
    // Also sync subcategory
    if (subcategoryFromUrl) {
      setSelectedSubcategory(subcategoryFromUrl);
    }
  }, [categoryId, categoriesFromUrl, categoryFromUrl, subcategoryFromUrl]);

  // Get subcategories for first selected category - sorted alphabetically
  const selectedCategoryData = selectedCategories.length === 1 
    ? categories.find(c => c.id === selectedCategories[0]) 
    : null;
  const subcategories = [...(selectedCategoryData?.subcategories || [])].sort((a, b) => a.localeCompare(b));
  
  // Sort categories alphabetically for dropdown
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategories([]);
      setSelectedSubcategory('all');
      setShowCategoryDropdown(false);
      return;
    }
    setSelectedCategories(prev => {
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
    // Close dropdown after selection
    setShowCategoryDropdown(false);
  };

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const params = {
          sort: sortBy,
          limit: 50,
        };
        // Support multiple categories - join with comma
        if (selectedCategories.length > 0) {
          params.category = selectedCategories.join(',');
        }
        if (selectedSubcategory !== 'all') params.subcategory = selectedSubcategory;
        if (search) params.search = search;
        if (filters.verified) params.verified = true;
        if (filters.escrow) params.escrow = true;
        if (filters.trending) params.trending = true;
        if (filters.minGoal) params.min_goal = parseFloat(filters.minGoal);
        if (filters.maxGoal) params.max_goal = parseFloat(filters.maxGoal);
        if (filters.country && filters.country !== 'all') params.country = filters.country;
        if (selectedLocation?.city) params.location = selectedLocation.city;

        const data = await campaignAPI.list(params);
        // Map API response to frontend format
        const formattedCampaigns = data.map(c => ({
          id: c.id,
          title: c.title,
          story: c.story,
          goal: c.goal,
          raised: c.raised,
          donors: c.donors,
          category: c.category,
          image: c.image,
          creator: c.creator,
          location: c.location,
          country: c.country || 'US',
          daysLeft: c.days_left,
          verified: c.verified,
          escrow: c.escrow,
          trending: c.trending,
          milestones: c.milestones,
        }));
        
        // Use real data only - no mock fallback
        setCampaigns(formattedCampaigns);
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
        setCampaigns([]);  // Show empty state instead of mock data
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedCategories, selectedSubcategory, sortBy, filters, search]);

  // Filter and sort campaigns
  const filteredCampaigns = React.useMemo(() => {
    let result = [...campaigns];
    
    // Filter by selected categories (multi-select)
    if (selectedCategories.length > 0) {
      result = result.filter(c => selectedCategories.includes(c.category));
    }
    
    // Filter by location
    if (selectedLocation) {
      const searchCity = selectedLocation.city?.toLowerCase() || selectedLocation.display?.split(',')[0]?.toLowerCase() || '';
      const searchCountry = selectedLocation.country?.toLowerCase() || selectedLocation.display?.split(',')[1]?.trim()?.toLowerCase() || '';
      
      result = result.filter(c => {
        const campaignLocation = (c.location || '').toLowerCase();
        const campaignCountry = (c.country || '').toLowerCase();
        
        return campaignLocation.includes(searchCity) || 
               campaignCountry.includes(searchCountry) ||
               campaignLocation.includes(searchCountry);
      });
    }
    
    // Sort by category
    if (sortBy === 'category') {
      const categoryOrder = categories.map(c => c.id);
      result.sort((a, b) => {
        const indexA = categoryOrder.indexOf(a.category);
        const indexB = categoryOrder.indexOf(b.category);
        return indexA - indexB;
      });
    }
    
    return result;
  }, [campaigns, selectedLocation, sortBy]);

  const activeFiltersCount = [
    filters.verified,
    filters.escrow,
    filters.trending,
    filters.minGoal,
    filters.maxGoal,
    filters.country && filters.country !== 'all',
    selectedLocation
  ].filter(Boolean).length;

  // Scroll to campaign list when filters change
  const scrollToCampaigns = () => {
    const campaignSection = document.getElementById('campaign-list');
    if (campaignSection) {
      campaignSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubcategoryChange = (subcategory) => {
    setSelectedSubcategory(subcategory);
    // Only scroll after subcategory is selected
    setTimeout(scrollToCampaigns, 100);
  };

  // Clear all category selections
  const clearCategories = () => {
    setSelectedCategories([]);
    setSelectedSubcategory('all');
    // Clear URL parameters
    setSearchParams({});
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
              {selectedCategories.length === 1 
                ? `${categories.find(c => c.id === selectedCategories[0])?.name || ''} ${t('campaignsLabel')}`
                : selectedCategories.length > 1 
                  ? `${selectedCategories.length} Categories Selected`
                  : `${t('discover')} ${t('campaignsLabel')}`}
            </h1>
            <p className="text-muted-foreground mb-6">
              {selectedCategories.length === 1
                ? categories.find(c => c.id === selectedCategories[0])?.description
                : t('browseCampaignsSubtitle')}
            </p>

            {/* Unified Search & Filter Bar */}
            <div className="bg-secondary/50 border border-border rounded-xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                {/* Search Input */}
                <div className="md:col-span-4">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('search')}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 pr-8 bg-background border-2 border-emerald-500/40"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Location Input */}
                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('location')}</label>
                  <LocationPicker
                    value={selectedLocation?.display || ''}
                    onChange={setSelectedLocation}
                    placeholder={t('cityOrCountry')}
                  />
                </div>

                {/* Category Multi-Select Dropdown */}
                <div className="md:col-span-3 relative category-dropdown-container">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('category')}</label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-background border-2 border-emerald-500/40 rounded-md text-sm"
                  >
                    <span className={selectedCategories.length > 0 ? 'text-foreground' : 'text-muted-foreground'}>
                      {selectedCategories.length === 0 
                        ? t('allCategories')
                        : selectedCategories.length === 1
                          ? categories.find(c => c.id === selectedCategories[0])?.name
                          : `${selectedCategories.length} selected`}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showCategoryDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-border flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Select multiple categories</span>
                        {selectedCategories.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              clearCategories(); 
                              setShowCategoryDropdown(false); 
                            }}
                            className="text-xs text-emerald-500 hover:text-emerald-400"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                      {sortedCategories.map((category) => {
                        const isSelected = selectedCategories.includes(category.id);
                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => toggleCategory(category.id)}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-secondary/50 ${
                              isSelected ? 'bg-emerald-500/10 text-emerald-400' : 'text-foreground'
                            }`}
                          >
                            <span>{category.name}</span>
                            {isSelected && <Check className="w-4 h-4 text-emerald-500" />}
                          </button>
                        );
                      })}
                      {/* View All Categories option */}
                      <div className="border-t border-border mt-2 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCategories([]);
                            setSelectedSubcategory('all');
                            setShowCategoryDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-secondary/50 ${
                            selectedCategories.length === 0 ? 'bg-emerald-500/10 text-emerald-400 font-medium' : 'text-foreground'
                          }`}
                        >
                          <span>{t('viewAllCategories') || 'View All Categories'}</span>
                          {selectedCategories.length === 0 && <Check className="w-4 h-4 text-emerald-500" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Subcategory Dropdown */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('subcategory')}</label>
                  <Select 
                    value={selectedSubcategory} 
                    onValueChange={handleSubcategoryChange}
                    disabled={selectedCategories.length !== 1 || subcategories.length === 0}
                  >
                    <SelectTrigger className="bg-background border-2 border-emerald-500/40">
                      <SelectValue placeholder={t('allSubcategories')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allSubcategories')}</SelectItem>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub} value={sub}>
                          {sub}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(selectedCategories.length > 0 || selectedSubcategory !== 'all' || selectedLocation || search) && (
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">{t('activeFilters')}:</span>
                  {search && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {t('search')}: {search}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch('')} />
                    </Badge>
                  )}
                  {selectedCategories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <Badge key={catId} variant="secondary" className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400">
                        {cat.name}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => toggleCategory(catId)} />
                      </Badge>
                    ) : null;
                  })}
                  {selectedSubcategory !== 'all' && (
                    <Badge variant="secondary" className="flex items-center gap-1 bg-teal-500/20 text-teal-400">
                      {selectedSubcategory}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedSubcategory('all')} />
                    </Badge>
                  )}
                  {selectedLocation && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {selectedLocation.display}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedLocation(null)} />
                    </Badge>
                  )}
                  {preferencesApplied && (user?.campaign_preferences?.length > 0 || user?.preferred_category) && (
                    <Badge variant="secondary" className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400">
                      <Filter className="w-3 h-3" /> Your Preferences
                    </Badge>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Clear all states
                      setSearch('');
                      setSelectedCategories([]);
                      setSelectedSubcategory('all');
                      setSelectedLocation(null);
                      setPreferencesApplied(false);
                      setFilters({
                        verified: false,
                        escrow: false,
                        trending: false,
                        minGoal: '',
                        maxGoal: '',
                        country: 'all',
                      });
                      
                      // Clear URL parameters - use navigate instead of pushState
                      setSearchParams({});
                    }}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters & Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Scroll anchor for category clicks */}
          <div id="campaign-list" className="scroll-mt-20"></div>

          {/* Results Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" /> {t('advancedFilters')}
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                {filteredCampaigns.length} {t('campaignsFound')}
              </span>
              <div className="flex border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-secondary' : 'bg-card'}`}
                >
                  <Grid3X3 className="w-4 h-4 text-foreground" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-secondary' : 'bg-card'}`}
                >
                  <List className="w-4 h-4 text-foreground" />
                </button>
              </div>
            </div>
          </div>

          {/* Filters Panel - Enhanced */}
          {showFilters && (
            <div className="bg-card rounded-xl p-6 mb-6 shadow-sm border border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Trust Filters */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">Trust & Safety</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="verified"
                        checked={filters.verified}
                        onCheckedChange={(checked) => setFilters({ ...filters, verified: checked })}
                      />
                      <label htmlFor="verified" className="text-sm text-foreground flex items-center cursor-pointer">
                        <BadgeCheck className="w-4 h-4 text-emerald-500 mr-1" /> Verified Only
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="escrow"
                        checked={filters.escrow}
                        onCheckedChange={(checked) => setFilters({ ...filters, escrow: checked })}
                      />
                      <label htmlFor="escrow" className="text-sm text-foreground flex items-center cursor-pointer">
                        <Shield className="w-4 h-4 text-blue-500 mr-1" /> Escrow Protected
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="trending"
                        checked={filters.trending}
                        onCheckedChange={(checked) => setFilters({ ...filters, trending: checked })}
                      />
                      <label htmlFor="trending" className="text-sm text-foreground flex items-center cursor-pointer">
                        <TrendingUp className="w-4 h-4 text-orange-500 mr-1" /> Trending
                      </label>
                    </div>
                  </div>
                </div>

                {/* Goal Range */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">Goal Range ($)</h4>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filters.minGoal}
                      onChange={(e) => setFilters({ ...filters, minGoal: e.target.value })}
                      className="w-24 bg-secondary"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filters.maxGoal}
                      onChange={(e) => setFilters({ ...filters, maxGoal: e.target.value })}
                      className="w-24 bg-secondary"
                    />
                  </div>
                </div>

                {/* Country Filter */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">Country</h4>
                  <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v })}>
                    <SelectTrigger className="bg-secondary">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="UK">United Kingdom</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="AU">Australia</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="FR">France</SelectItem>
                      <SelectItem value="IN">India</SelectItem>
                      <SelectItem value="BR">Brazil</SelectItem>
                      <SelectItem value="JP">Japan</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick Presets */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">Quick Filters</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ ...filters, verified: true, escrow: true })}
                      className="text-xs"
                    >
                      Most Trusted
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilters({ ...filters, minGoal: '', maxGoal: '5000' });
                      }}
                      className="text-xs"
                    >
                      Under $5k
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilters({ ...filters, minGoal: '10000', maxGoal: '' });
                      }}
                      className="text-xs"
                    >
                      $10k+
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedLocation && (
                <Badge variant="secondary" className="px-3 py-1">
                  <MapPin className="w-3 h-3 mr-1" /> {selectedLocation.display}
                  <button onClick={() => setSelectedLocation(null)} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.verified && (
                <Badge variant="secondary" className="px-3 py-1">
                  <BadgeCheck className="w-3 h-3 mr-1" /> Verified
                  <button onClick={() => setFilters({ ...filters, verified: false })} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.escrow && (
                <Badge variant="secondary" className="px-3 py-1">
                  <Shield className="w-3 h-3 mr-1" /> Escrow
                  <button onClick={() => setFilters({ ...filters, escrow: false })} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.trending && (
                <Badge variant="secondary" className="px-3 py-1">
                  <TrendingUp className="w-3 h-3 mr-1" /> Trending
                  <button onClick={() => setFilters({ ...filters, trending: false })} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.country && filters.country !== 'all' && (
                <Badge variant="secondary" className="px-3 py-1">
                  Country: {filters.country}
                  <button onClick={() => setFilters({ ...filters, country: 'all' })} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {(filters.minGoal || filters.maxGoal) && (
                <Badge variant="secondary" className="px-3 py-1">
                  Goal: {filters.minGoal ? `$${filters.minGoal}` : '$0'} - {filters.maxGoal ? `$${filters.maxGoal}` : '∞'}
                  <button onClick={() => setFilters({ ...filters, minGoal: '', maxGoal: '' })} className="ml-2 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              <button
                onClick={() => {
                  setFilters({ verified: false, escrow: false, trending: false, minGoal: '', maxGoal: '', country: 'all' });
                  setSelectedLocation(null);
                }}
                className="text-sm text-emerald-500 hover:text-emerald-400"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Campaigns Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : filteredCampaigns.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {filteredCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  variant={viewMode === 'grid' ? 'featured' : 'compact'}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">No campaigns found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search or filter to find what you are looking for
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setSelectedCategories([]);
                  setSelectedLocation(null);
                  setFilters({ verified: false, escrow: false, trending: false, minGoal: '', maxGoal: '', country: 'all' });
                }}
              >
                Clear all filters
              </Button>
            </div>
          )}

          {/* Load More */}
          {filteredCampaigns.length > 0 && (
            <div className="text-center mt-12">
              <Button variant="outline" size="lg" className="rounded-full px-8">
                Load More Campaigns
              </Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DiscoverPage;
